import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { IRole } from "src/core/shared/api/roles.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";

// user types
export type CreateUserParams = Pick<IUser, "email" | "firstName" | "lastName" | "phoneNumber" | "roleId" | "customerId" | "defaultBranchId" | "userBranches" | "passwordAuthDisabled" | "useSubdomain">;
export type UpdateUserParams = Pick<IUserUpdate, "id"> & Partial<IUserUpdate>;
export type UpdateUserSettingsParams = Pick<IUserUpdate, "id"> & { updatedSettings: Array<Omit<IUserSettingAttributes, "id">>};
export type ListUsersParams = { pagination: PaginatedParams<IUser>; filters: FilterParams<IUser> & { userGroupId?: number } };
export type ListBranchUsersParams = { pagination: PaginatedParams<IUser>; filters: FilterParams<IUser>; dropId: number; };
// user group types
export type ListUserGroupsParams = { pagination: PaginatedParams<IUserGroup>; filters: FilterParams<IUserGroup> };
export type CreateUserGroupParams = Omit<IUserGroup, "id">;
export type UpdateUserGroupUser = Partial<IUserGroupUser>;
export type UpdateUserGroupParams = Omit<Partial<IUserGroup>, "userGroupUsers"> & { userGroupUsers?: UpdateUserGroupUser[]};
export type CreateEditUserGroup = Omit<IUserGroup, "id"> & Partial<Pick<IUserGroup, "id">>;

type UserEndpointConfig = {
  LIST_USERS: EndpointConfig<
    ListUsersParams,
    PaginatedData<IUser>
  >;
  LIST_BRANCH_USERS: EndpointConfig<
    ListBranchUsersParams,
    PaginatedData<IUser>
  >;
  GET_USER: EndpointConfig<
    {id: number},
    IUser
  >;
  CREATE_USER: EndpointConfig<
    CreateUserParams,
    IUser
  >;
  UPDATE_USER: EndpointConfig<
    UpdateUserParams,
    IUser
  >;
  UPDATE_USER_SETTINGS: EndpointConfig<
    UpdateUserSettingsParams,
    IUser
  >;
  DELETE_USER: EndpointConfig<
    {id: number},
    {message: string}
  >;
  RESEND_INVITE: EndpointConfig<
    {id: number},
    IUser
  >;

  // USER GROUP ENDPOINT CONFIG
  LIST_USER_GROUPS: EndpointConfig<
    ListUserGroupsParams,
    PaginatedData<IUserGroup>
  >;
  GET_USER_GROUP: EndpointConfig<
    {id: number},
    IUserGroup
  >;
  CREATE_USER_GROUP: EndpointConfig<
    CreateEditUserGroup,
    IUserGroup
  >;
  UPDATE_USER_GROUP: EndpointConfig<
    UpdateUserGroupParams,
    IUserGroup
  >;
  DELETE_USER_GROUP: EndpointConfig<
    {id: number},
    {message: string}
  >;
  EXPORT_CUSTOMER_USER: EndpointConfig<
    {customerIds: number[]},
    {message: string}
  >;

};

const BASE_URL_USERS = `${process.env.REACT_APP_API_URL}/core/users`;
const BASE_URL_GROUPS = `${process.env.REACT_APP_API_URL}/core/users/groups`;

const requests: RequestConfig<UserEndpointConfig> = {
  LIST_USERS: params => ({
    url: `${BASE_URL_USERS}`,
    method: "GET",
    params
  }),
  LIST_BRANCH_USERS: params => ({
    url: `${BASE_URL_USERS}/branches/${params.dropId}`,
    method: "GET",
    params
  }),
  GET_USER: data => ({
    url: `${BASE_URL_USERS}/${data.id}`,
    method: "GET"
  }),
  CREATE_USER: data => ({
    url: `${BASE_URL_USERS}`,
    method: "POST",
    data
  }),
  UPDATE_USER: data => ({
    url: `${BASE_URL_USERS}`,
    method: "PATCH",
    data
  }),
  UPDATE_USER_SETTINGS: ({ id, updatedSettings }) => ({
    url: `${BASE_URL_USERS}/${id}/settings`,
    method: "PATCH",
    data: updatedSettings
  }),
  DELETE_USER: data => ({
    url: `${BASE_URL_USERS}/${data.id}`,
    method: "DELETE"
  }),
  RESEND_INVITE: data => ({
    url: `${BASE_URL_USERS}/${data.id}/resend-invite`,
    method: "POST"
  }),
  EXPORT_CUSTOMER_USER: data => ({
    url: `${BASE_URL_USERS}/customers/all/export`,
    method: "POST",
    data
  }),

  // USER GROUP REQUEST CONFIG
  LIST_USER_GROUPS: params => ({
    url: `${BASE_URL_GROUPS}/all`,
    method: "GET",
    params
  }),
  GET_USER_GROUP: data => ({
    url: `${BASE_URL_GROUPS}/${data.id}`,
    method: "GET"
  }),
  CREATE_USER_GROUP: data => ({
    url: `${BASE_URL_GROUPS}`,
    method: "POST",
    data
  }),
  UPDATE_USER_GROUP: data => ({
    url: `${BASE_URL_GROUPS}/${data.id}`,
    method: "PATCH",
    data
  }),
  DELETE_USER_GROUP: data => ({
    url: `${BASE_URL_GROUPS}/${data.id}`,
    method: "DELETE"
  })
};

export const useUserApi: UseApiRequest<UserEndpointConfig> = name => useAxios(requests[ name ], name);

export enum UserSettingName {
  EmailNotifications = "email_notifications",
  PlatformNotifications = "platform_notifications",
  ErrorNotifications = "error_notifications",
  SuggestedOrders = "suggested_orders",
  OrderCutOffReminders = "order_cutoff_reminders"
}

export enum OrderCutOffRemindersKeys {
  OnlineBanner = "onlineBanner",
  Email = "email",
  Text = "text"
}

export interface IUser {
  id: number;
  cognitoId: string;
  createdAt: Date;
  updatedAt: Date;
  lastLoggedIn: Date | null;
  email: string;
  phoneNumber: string | null;
  mFAMethod?: string;
  firstName: string;
  lastName?: string;
  roleId: number;
  customerId?: number;
  defaultBranchId?: number;
  userBranches?: IDrop[];
  status?: string;
  passwordAuthDisabled: boolean;
  useSubdomain?: string;
  customer?: ICustomer;
  userSettings?: IUserSettingAttributes[];
  userGroups?: IUserGroup[];
  role?: IRole;
}

export interface IUserSettingAttributes {
  id: number;
  userId: number;
  settingName: UserSettingName;
  enabled: boolean;
  options?: UserSettingOptions;
}
export interface IUserUpdate extends Omit<IUser, "userSettings"> {
  userSettings?: UserSettingName[];
  userGroupIds?: number[];
}

export interface IUserBranch {
  id?: number;
  userId: number;
  branchId: number;
}

export interface IUserGroupUser {
  id: number;
  userId: number;
  userGroupId: number;
  user?: IUser;
}

export interface IUserGroup {
  id: number;
  name: string;
  managerId?: number | null;
  userGroupUsers?: IUserGroupUser[];
  manager?: IUser;
}

export interface ILoginEntities {
  user: IUser;
  customer?: ICustomer;
  branch?: IDrop;
}

export interface UserSettingOptions {
  [OrderCutOffRemindersKeys.OnlineBanner]: boolean;
  [OrderCutOffRemindersKeys.Email]: boolean;
}
